import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { io } from "socket.io-client";
import { API, useAuth } from "../App";
import { 
  MessageCircle, X, Send, Bot, User, Sparkles, 
  Loader2, UserRound, ArrowRight, Minimize2, Wifi, WifiOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate unique session ID
const generateSessionId = () => {
  const stored = localStorage.getItem("rateshare_chat_session");
  if (stored) return stored;
  const newId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem("rateshare_chat_session", newId);
  return newId;
};

const ChatWidget = () => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isTransferred, setIsTransferred] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [hasAdmin, setHasAdmin] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [useRealtime, setUseRealtime] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Initialize session
  useEffect(() => {
    setSessionId(generateSessionId());
  }, []);

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!isOpen || !useRealtime) return;

    const socket = io(BACKEND_URL, {
      path: '/api/ws',
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket.IO connected');
      setIsConnected(true);
      
      // Join chat room
      if (sessionId) {
        socket.emit('join_chat', { session_id: sessionId });
      }
    });

    socket.on('disconnect', () => {
      console.log('Socket.IO disconnected');
      setIsConnected(false);
    });

    socket.on('chat_joined', (data) => {
      console.log('Joined chat:', data);
      if (data.messages?.length > 0) {
        setMessages(data.messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        })));
        setShowWelcome(false);
      }
      setHasAdmin(data.has_admin);
    });

    socket.on('new_message', (data) => {
      const msg = data.message;
      setMessages(prev => [...prev, {
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }]);
      setIsTyping(false);
    });

    socket.on('admin_joined', (data) => {
      setHasAdmin(true);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'system',
        content: data.message,
        timestamp: new Date().toISOString()
      }]);
    });

    socket.on('admin_disconnected', (data) => {
      setHasAdmin(false);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'system',
        content: data.message,
        timestamp: new Date().toISOString()
      }]);
    });

    socket.on('user_typing', () => {
      setIsTyping(true);
    });

    socket.on('user_stopped_typing', () => {
      setIsTyping(false);
    });

    socket.on('chat_closed', (data) => {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'system',
        content: data.message,
        timestamp: new Date().toISOString()
      }]);
    });

    socket.on('error', (data) => {
      console.error('Socket error:', data.message);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isOpen, useRealtime, sessionId, token]);

  // Load chat history via HTTP (fallback)
  const loadHistory = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/chatbot/history/${sessionId}`);
      if (res.data.messages?.length > 0) {
        setMessages(res.data.messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.created_at
        })));
        setShowWelcome(false);
      }
    } catch (error) {
      console.log("No previous chat history");
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen && sessionId && !useRealtime) {
      loadHistory();
    }
  }, [isOpen, sessionId, loadHistory, useRealtime]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Handle typing indicator
  const handleTyping = () => {
    if (useRealtime && socketRef.current?.connected) {
      socketRef.current.emit('typing_start', { session_id: sessionId });
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current?.emit('typing_stop', { session_id: sessionId });
      }, 2000);
    }
  };

  // Send message
  const sendMessage = async (text = inputValue.trim()) => {
    if (!text || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setShowWelcome(false);

    // Real-time mode: use Socket.IO
    if (useRealtime && socketRef.current?.connected) {
      socketRef.current.emit('send_message', {
        session_id: sessionId,
        message: text
      });
      return;
    }

    // HTTP fallback: AI chatbot
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/chatbot/message`, {
        message: text,
        session_id: sessionId
      });

      const botMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: res.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Désolé, je rencontre un problème technique. Réessayez ou contactez-nous à contact@rateshare.fr",
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Transfer to human admin (switches to real-time mode)
  const transferToAdmin = async () => {
    setIsLoading(true);
    try {
      // Create support ticket via HTTP
      await axios.post(`${API}/chatbot/transfer-to-admin`, {
        message: "L'utilisateur souhaite parler à un conseiller humain",
        session_id: sessionId
      });
      
      setIsTransferred(true);
      setUseRealtime(true); // Switch to real-time mode
      
      const systemMessage = {
        id: Date.now().toString(),
        role: "system",
        content: "✅ Votre demande a été transmise. Un conseiller va rejoindre la conversation. Patientez quelques instants...",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, systemMessage]);
    } catch (error) {
      console.error("Transfer error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Quick action buttons
  const quickActions = [
    { label: "Comment ça marche ?", icon: Sparkles },
    { label: "Tarifs", icon: ArrowRight },
    { label: "Créer un compte", icon: User }
  ];

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        data-testid="chat-widget-button"
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/25 flex items-center justify-center hover:scale-110 transition-transform ${isOpen ? 'hidden' : ''}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <MessageCircle size={24} />
        {/* Notification dot */}
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-zinc-950 animate-pulse" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chat-widget-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] h-[600px] max-h-[80vh] rounded-2xl bg-zinc-900/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center">
                    {hasAdmin ? <UserRound size={20} className="text-white" /> : <Bot size={20} className="text-white" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">
                      {hasAdmin ? "Support RateShare" : "Assistant RateShare"}
                    </h3>
                    <div className="flex items-center gap-1.5">
                      {useRealtime ? (
                        isConnected ? (
                          <>
                            <Wifi size={12} className="text-green-400" />
                            <span className="text-xs text-green-400">Temps réel</span>
                          </>
                        ) : (
                          <>
                            <WifiOff size={12} className="text-yellow-400" />
                            <span className="text-xs text-yellow-400">Reconnexion...</span>
                          </>
                        )
                      ) : (
                        <>
                          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                          <span className="text-xs text-zinc-400">En ligne</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <Minimize2 size={18} className="text-zinc-400" />
                  </button>
                  <button
                    data-testid="chat-widget-close"
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <X size={18} className="text-zinc-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Welcome Message */}
              {showWelcome && messages.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={16} className="text-pink-400" />
                      <span className="text-sm font-medium text-white">Bienvenue sur RateShare !</span>
                    </div>
                    <p className="text-sm text-zinc-300">
                      Je suis votre assistant virtuel. Comment puis-je vous aider aujourd'hui ?
                    </p>
                  </div>

                  {/* Quick Actions */}
                  <div className="space-y-2">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Questions fréquentes</p>
                    {quickActions.map((action) => (
                      <button
                        key={action.label}
                        onClick={() => sendMessage(action.label)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-all text-left group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
                          <action.icon size={16} className="text-pink-400" />
                        </div>
                        <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">
                          {action.label}
                        </span>
                        <ArrowRight size={14} className="ml-auto text-zinc-500 group-hover:text-pink-400 transition-colors" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Chat Messages */}
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                    {/* Avatar */}
                    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                      msg.role === "user" 
                        ? "bg-blue-500/20" 
                        : msg.role === "admin"
                        ? "bg-green-500/20"
                        : msg.role === "system"
                        ? "bg-yellow-500/20"
                        : "bg-pink-500/20"
                    }`}>
                      {msg.role === "user" ? (
                        <User size={14} className="text-blue-400" />
                      ) : msg.role === "admin" ? (
                        <UserRound size={14} className="text-green-400" />
                      ) : msg.role === "system" ? (
                        <Sparkles size={14} className="text-yellow-400" />
                      ) : (
                        <Bot size={14} className="text-pink-400" />
                      )}
                    </div>

                    {/* Message Bubble */}
                    <div className={`rounded-2xl px-4 py-2.5 ${
                      msg.role === "user"
                        ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white"
                        : msg.role === "system"
                        ? "bg-yellow-500/10 border border-yellow-500/20 text-yellow-200"
                        : msg.role === "admin"
                        ? "bg-green-500/10 border border-green-500/20 text-green-200"
                        : msg.isError
                        ? "bg-red-500/10 border border-red-500/20 text-red-200"
                        : "bg-white/5 border border-white/10 text-zinc-200"
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Typing indicator */}
              {(isLoading || isTyping) && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-start gap-2"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${hasAdmin ? 'bg-green-500/20' : 'bg-pink-500/20'}`}>
                    {hasAdmin ? (
                      <UserRound size={14} className="text-green-400" />
                    ) : (
                      <Bot size={14} className="text-pink-400" />
                    )}
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Transfer to Human Button */}
            {messages.length > 2 && !isTransferred && !useRealtime && (
              <div className="px-4 pb-2">
                <button
                  data-testid="transfer-to-human-btn"
                  onClick={transferToAdmin}
                  disabled={isLoading}
                  className="w-full py-2 px-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-medium hover:bg-green-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <UserRound size={16} />
                  Parler à un conseiller
                </button>
              </div>
            )}

            {/* Real-time status banner */}
            {useRealtime && !hasAdmin && (
              <div className="px-4 pb-2">
                <div className="py-2 px-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs text-center">
                  En attente d'un conseiller...
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t border-white/10 bg-black/20">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  data-testid="chat-input"
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    handleTyping();
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder={hasAdmin ? "Écrivez à l'agent..." : "Écrivez votre message..."}
                  disabled={isLoading}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-pink-500/50 transition-colors disabled:opacity-50"
                />
                <button
                  data-testid="chat-send-btn"
                  onClick={() => sendMessage()}
                  disabled={!inputValue.trim() || isLoading}
                  className="w-11 h-11 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-pink-500/25 transition-all"
                >
                  {isLoading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-2 text-center">
                {useRealtime ? "Chat temps réel • Socket.IO" : "Propulsé par l'IA • Support 24/7"}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
